import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Chip,
    Box,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import {DownloadCSVButton} from "../common/SubmitButton";

export interface HalfHourUploadFormat {
    headerFields: string[];
    footer?: string;
    timeZone?: string;
}

interface UploadFormatProps {
    formats: HalfHourUploadFormat[];
    dataTestId?: string;
}

 const downloadCsvTemplate = (format: HalfHourUploadFormat, formatIndex: number) => {
    // Create CSV content with headers
    const csvContent = format.headerFields.join(',') + '\n';

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `upload_template_format_${formatIndex + 1}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
};

export const UploadFormat = ({ formats, dataTestId }: UploadFormatProps) => {


    if (!formats || formats.length === 0) {
        return (
            <Card variant="outlined" data-testid={dataTestId}>
                <CardContent>
                    <Typography variant="body1" color="text.secondary" align="center">
                        No upload formats available
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box data-testid={dataTestId}>
            <Typography variant="h6" gutterBottom>
                Accepted Upload Formats
            </Typography>

            {formats.map((format, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1">
                                Format {index + 1}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {format.timeZone && (
                                    <Chip
                                        label={`Time Zone: ${format.timeZone}`}
                                        size="small"
                                        color="primary"
                                    />
                                )}
                                <DownloadCSVButton
                                    onSubmit={() => Promise.resolve(downloadCsvTemplate(format, index))}
                                    dataTestId={`download-template-${index}`}
                                />
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="subtitle2" gutterBottom>
                            Expected Header Fields:
                        </Typography>

                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Field Name</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {format.headerFields.map((field, fieldIndex) => (
                                        <TableRow key={fieldIndex}>
                                            <TableCell width={50}>{fieldIndex + 1}</TableCell>
                                            <TableCell>{field}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {format.footer && (
                            <>
                                <Typography variant="subtitle2" gutterBottom>
                                    Footer Format:
                                </Typography>
                                <Box
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        p: 1.5,
                                        borderRadius: 1,
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {format.footer}
                                </Box>
                            </>
                        )}
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
};